import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { ToastComponent } from '../shared/toast/toast.component';
import { RecordService } from '../services/record.service';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { PoliticianService } from '../services/politician.service';
import { Record } from '../shared/models/record.model';
import { User } from '../shared/models/user.model';
import { Client } from '../shared/models/client.model';
import { ee, activities, formats, statuses, dateRegexp, perPage50 } from '../shared/constants';
import { removeTooltipElement, scrollTo } from '../shared/functions';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-records',
  templateUrl: './records.component.html',
  styleUrls: ['./records.component.scss'],
  standalone: false
})
export class RecordsComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private recordService = inject(RecordService);
  private userService = inject(UserService);
  private clientService = inject(ClientService);
  private politicianService = inject(PoliticianService);
  toast = inject(ToastComponent);


  isLoading = {
    records: true
  };
  isSearching = false;
  canAssignRecord = false;
  activities = activities;
  statuses = statuses;
  formats = formats;
  dateRegexp = dateRegexp;
  perPage = perPage50;
  // Filters
  user = '';
  politician = '';
  client = '';
  organization = '';
  party = '';
  media = '';
  title = '';
  activity = '';
  status = '';
  startDate = '';
  endDate = '';
  format = '';
  followup = '';
  notes = '';
  followupnotes = '';
  assigned = '';
  created_by = '';
  // Arrays
  records: Record[] = [];
  recordAdded = false;
  team: User[] = [];
  clients: Client[] = [];
  parties = [];
  partiesEU = [];
  mediaCompanies = [];
  count = 0;
  page = 0;
  currentContent = 'track-record';
  menuItems = [
    { name: 'Records', link: 'track-record', icon: 'list' },
    { name: 'Planning / Review', link: 'planning-review', icon: 'file-text-o' },
    { name: 'UTOPIA Studios - URANIA', link: 'utopia-studios', icon: 'video-camera' },
    { name: 'App updates', link: 'app-updates', icon: 'bullhorn' },
    { name: 'My records', link: 'my-records', icon: 'user' },
    { name: 'Records I assigned', link: 'records-i-assigned', icon: 'user-plus' },
    { name: 'My todos', link: 'my-todos', icon: 'square-o' },
    { name: 'My doings', link: 'my-doings', icon: 'hourglass-half' },
    { name: '', link: '', icon: '' }, // Workaround to create a separator
    { name: 'Add new', link: 'add-new-record', icon: 'plus-circle' },
  ];

  ngOnInit(): void {
    ee.on('changeContent', (data: string) => this.changeContentFn(data));
    ee.on('getRecords', (page?: number) => this.search(page));
    ee.on('setPoliticianFilter', (politician: any) => {
      this.politician = politician;
      this.search();
    });
    ee.on('recordAdded', (recordAdded: boolean) => this.recordAdded = recordAdded);
    this.search();
    this.getUsers();
    this.getClients();
    this.getParties();
    this.getMedias();
  }

  ngOnDestroy(): void {
    ee.off('changeContent', () => {});
    ee.off('getRecords', () => {});
    ee.off('setPoliticianFilter', () => {});
    ee.off('recordAdded', () => {});
    removeTooltipElement();
  }

  changeContentFn(content: string): void {
    this.currentContent = content;
    this.recordAdded = false;
    this.resetFilters();
    scrollTo(0);
    switch (content) {
      case 'track-record':
        this.search();
        break;
      case 'planning-review':
        this.activity = 'Planning / Review';
        this.search();
        break;
      case 'utopia-studios':
        this.activity = 'UTOPIA Studios - URANIA';
        this.search();
        break;
      case 'app-updates':
        this.activity = 'Tech';
        this.search();
        break;
      case 'my-records':
        this.user = this.auth.currentUser._id;
        this.search();
        break;
      case 'records-i-assigned':
        this.created_by = this.auth.currentUser._id;
        this.assigned = '1';
        this.search();
        break;
      case 'my-todos':
        this.user = this.auth.currentUser._id;
        this.status = 'Todo';
        this.search();
        break;
      case 'my-doings':
        this.user = this.auth.currentUser._id;
        this.status = 'Doing';
        this.search();
        break;
      case 'add-new-record':
        this.canAssignRecord = this.auth.currentUser.permissions.includes('assignRecord');
        this.addAnotherRecord();
        break;
    }
  }

  resetFilters(): void {
    this.user = '';
    this.politician = '';
    this.client = '';
    this.title = '';
    this.activity = '';
    this.status = '';
    this.startDate = '';
    this.endDate = '';
    this.format = '';
    this.followup = '';
    this.notes = '';
    this.followupnotes = '';
    this.created_by = '';
    this.assigned = '';
  }

  search(page?: number): void {
    this.isLoading.records = true;
    this.isSearching = true;
    this.page = page || 0;
    const params = {
      startDate: this.startDate,
      endDate: this.endDate,
      _user: this.user,
      _users: this.user,
      _politicians: this.politician,
      _clients: this.client,
      _organizations: this.organization,
      _party: this.party,
      _media: this.media,
      title: this.title,
      activity: this.activity,
      status: this.status,
      format: this.format,
      followup: this.followup,
      notes: this.notes,
      _created_by: this.created_by,
      assigned: this.assigned,
      page: this.page,
      perPage: this.perPage,
    };
    this.recordService.search(params)
      .pipe(finalize(() => this.isLoading.records = false))
      .subscribe({
        next: res => {
          this.records = res.results;
          this.count = res.count;
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getUsers(): void {
    this.userService.search({})
      .subscribe({
        next: res => this.team = res.results,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getClients(): void {
    this.clientService.search({ active: '1' })
      .subscribe({
        next: res => this.clients = res.results,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getParties(): void {
    this.politicianService.getParties()
      .subscribe({
        next: res => {
          this.parties = res.filter(party => !party.parlamento_eu);
          this.partiesEU = res.filter(party => party.parlamento_eu);
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getMedias(): void {
    this.politicianService.getMedias()
      .subscribe({
        next: res => this.mediaCompanies = res,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  goToTrackRecords(): void {
    this.currentContent = 'track-record';
    this.recordAdded = false;
    this.resetFilters();
    this.search();
  }

  addAnotherRecord(): void {
    this.recordAdded = false;
    /* this.addRecordForm.patchValue({ _users: [] });
    this.addRecordForm.patchValue({ _politicians: [] });
    this.addRecordForm.patchValue({ _clients: [] });
    this.addRecordForm.patchValue({ _organizations: [] }); */
  }

}
